import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
  resolveConflicts,
} from '@/utils/apiParamsUtils';

/**
 * Creates a new sector with the provided data.
 * @param {Object} payload - The data for the new sector.
 * @returns {Promise<Object>} A promise that resolves with the created sector data.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewSector = async (payload) => {
  try {
    const response = await axiosClient.post('/sectors/create', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create a new sector:', error);
    throw error;
  }
};

/**
 * Retrieves all sectors.
 * @returns {Promise<Object[]>} A promise that resolves with an array of sector objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllSectors = async (params) => {
  const filters = prepareFilters(params, {
    company: 'default',
    companies: 'array',
    sectorName: 'default',
    hrSpecialists: 'default',
    createdAt: 'range',
    updatedAt: 'range',
    emailReceivers: 'default',
    'company.clientsAdmins.clients': 'default',
  });

  const sorters = prepareSorters(params, {
    sectorName: 'sortSectorName',
    clients: 'sortClients',
    hrSpecialist: 'sortHrSpecialist',
    company: 'sortCompany',
  });

  const pagination = preparePagination(params);

  const conflictRules = [
    { keys: ['company', 'companies'], priority: 'companies' },
  ];

  const filtersResolved = resolveConflicts(filters, conflictRules);

  try {
    const response = await axiosClient.get('/sectors', {
      params: {
        filter: filtersResolved,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve all sectors:', error);
    throw error;
  }
};

/**
 * Retrieves dropdown all sectors.
 * @returns {Promise<Object[]>} A promise that resolves with an array of sector objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveDropdownAllSectors = async (params) => {
  try {
    const response = await axiosClient.get('/sectors/dropdown', {
      params: {
        filter: params?.filter,
        select: params?.select,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.graph) {
          params.graph = JSON.stringify(params.graph);
        }

        if (params.select) {
          params.select = JSON.stringify(params.select);
        }

        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve dropdown sectors:', error);
    throw error;
  }
};

/**
 * Updates an existing sector by their ID with the provided data.
 * @param {string} sectorId - The unique identifier of the sector.
 * @param {Object} payload - The updated data for the sector.
 * @returns {Promise<Object>} A promise that resolves with the updated sector data.
 * @throws Will throw an error if the update request fails.
 */
export const updateSectorById = async (sectorId, payload) => {
  try {
    const response = await axiosClient.put(`/sectors/${sectorId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Failed to update sector with ID ${sectorId}:`, error);
    throw error;
  }
};

/**
 * Retrieves a sector by its ID.
 * @param {string} sectorId - The unique identifier of the sector.
 * @returns {Promise<Object>} A promise that resolves with the sector data.
 * @throws Will throw an error if the request fails.
 */
export const getSectorById = async (sectorId) => {
  try {
    const response = await axiosClient.get(`/sectors/${sectorId}`);
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve sector with ID ${sectorId}:`, error);
    throw error;
  }
};

/**
 * Deletes a sector by their ID.
 * @param {string} sectorId - The unique identifier of the sector.
 * @returns {Promise<Object>} A promise that resolves when the sector is deleted.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteSectorById = async (sectorId) => {
  try {
    const response = await axiosClient.delete(`/sectors/${sectorId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete sector with ID ${sectorId}:`, error);
    throw error;
  }
};

/**
 * Retrieves sector structure (tree view) for a given company.
 * @param {string} companyId - The company ID.
 * @returns {Promise<Object>} { sectors: [...], unassigned: [...] }
 */
export const getSectorStructure = async (companyId) => {
  try {
    const response = await axiosClient.get('/sectors/structure', {
      params: { company: companyId },
    });
    return response.data?.data;
  } catch (error) {
    console.error('Failed to retrieve sector structure:', error);
    throw error;
  }
};

/**
 * Reassigns employees to different sectors.
 * @param {Array<{ employeeId: string, newSectorId: string|null }>} assignments
 * @returns {Promise<Object>}
 */
export const reassignEmployeeSectors = async ({
  assignments,
  hrAssignments,
}) => {
  try {
    const response = await axiosClient.put('/sectors/reassign', {
      assignments,
      hrAssignments,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to reassign employee sectors:', error);
    throw error;
  }
};

export const exportSectorStructure = async (companyId) => {
  try {
    const response = await axiosClient.get('/sectors/structure/export', {
      params: { company: companyId },
      responseType: 'blob',
      timeout: 60000,
    });

    const contentType = response.headers['content-type'];
    const disposition = response.headers['content-disposition'];

    const { extractFileNameFromDisposition, saveBlobAsFile } = await import(
      '@/utils/exportUtils'
    );
    const fileName =
      extractFileNameFromDisposition(disposition) || 'sector-structure.xlsx';

    saveBlobAsFile(response.data, contentType, fileName);

    return { message: 'Document downloaded successfully.' };
  } catch (error) {
    console.error('Failed to export sector structure:', error);
    throw error;
  }
};

const SectorsService = {
  createNewSector,
  retrieveAllSectors,
  retrieveDropdownAllSectors,
  updateSectorById,
  getSectorById,
  deleteSectorById,
  getSectorStructure,
  reassignEmployeeSectors,
  exportSectorStructure,
};

export default SectorsService;
